
import { Component, Emit, Mixins } from "vue-property-decorator";
import { Getter, namespace, State } from "vuex-class";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import vselect3 from "vselect3";
import { Datetime } from "vue-datetime";
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";
import { get } from "lodash";

import api from "@/services/api.service";

import { FeaturesList, RootState, UserPermissions, VueBootstrapRadioField, Region } from "@/store/types";
import { ArmDisarmState, GroupIsOnTestOverride, AreaSelected } from "@/store/arm-disarm-area/types";
import { Group, GroupStatus } from "@/store/eventqueue/types";
import { MaskReason, SiteMonitorState } from "@/store/site-monitor/types";

import AreaAlarms from "@/components/alarm-points/AreaAlarms.vue";
import PutOnTest from "@/components/alarm-points/PutOnTest.vue";
import { getLocalRegion } from "@/utils/region-utils";

const AlarmPoints = namespace("alarmPoints");
const ArmDisarm = namespace("armDisarm");
const SiteMonitor = namespace("siteMonitor");

@Component({
	components: {
		"area-alarms": AreaAlarms,
		"date-time": Datetime,
		"put-on-test": PutOnTest,
		"v-select-3": vselect3,
		"vue-perfect-scrollbar": VuePerfectScrollbar
	},
	validations: {
		disarmReason: { required }
	}
})
export default class GroupIsArmedModal extends Mixins(validationMixin) {
	@Getter("getPermissions") private permissions: UserPermissions;
	@State((state: RootState) => state.featuresList) private featuresList: FeaturesList;

	@AlarmPoints.Mutation private setSelectedArea: (area: Group) => Promise<void>;

	@ArmDisarm.State((state: ArmDisarmState) => state.selectedGroup) private selectedGroup: Group;
	@ArmDisarm.State((state: ArmDisarmState) => state.selectedRegion) private region: Region;
	@ArmDisarm.Mutation private setGroupIsArmedModalVisibility: (visible: boolean) => void;
	@ArmDisarm.Mutation private setGroupIsOnTestOverride: (groupIsOnTestOverride: GroupIsOnTestOverride) => void;

	@SiteMonitor.State((state: SiteMonitorState) => state.GroupMaskReasons) private groupMaskReasons: MaskReason[];
	@SiteMonitor.Action private loadMaskReasonsForGroup: ({ groupID: number, regionUrl: string }) => Promise<void>;

	private disarmUntil: string = new Date(Date.now()).toISOString();
	private disarmReason: MaskReason = null;
	private selectedOption: string = "";

	private disarmingOptions: VueBootstrapRadioField[] = [{ text: "Disarm", value: "disarm" }];

	private async created(): Promise<void> {
		if (this.showPutOnTestOption) {
			this.disarmingOptions.push({ text: "Put On Test", value: "put-on-test" });
		}

		if (this.showDisarmedAlarmsOption) {
			this.setSelectedArea(this.selectedGroup);
			this.disarmingOptions.push({ text: "View Disarmed Alarms", value: "view-disarmed-alarms" });
		}

		if (this.userCanDisarm) {
			await this.loadMaskReasonsForGroup({ groupID: this.selectedGroup.groupID, regionUrl: this.region?.url });
		}
	}

	private get modalTitle(): string {
		return `${this.selectedGroup.title} - ${
			this.selectedGroup.status === GroupStatus.Partial ? "Partially Disarmed" : "Armed"
		}`;
	}

	private handleModalHidden(): void {
		this.setGroupIsArmedModalVisibility(false);
	}

	private get showSelectionOptions(): boolean {
		return this.disarmingOptions.length > 1;
	}

	private get showDisarmOptionsContent(): boolean {
		return this.selectedOption === "disarm" || !this.showSelectionOptions;
	}

	private get showDisarmButton(): boolean {
		return this.showDisarmOptionsContent && this.userCanDisarm;
	}

	private get userCanDisarm(): boolean {
		return this.permissions.canDisarmSites || this.canDisarmExtended;
	}

	private get canDisarmExtended(): boolean {
		return this.permissions.canDisarmSitesExtended;
	}

	private get showPutOnTestOption(): boolean {
		return get(this.featuresList, ["Alarms", "EventQueue", "SeeSiteArmedState", "OnTest"]);
	}

	private get showPutOnTestContent(): boolean {
		return this.selectedOption === "put-on-test";
	}

	private get userCanPlaceSitesOnTest(): boolean {
		return this.permissions.canSetSiteToOnTest;
	}

	private get showDisarmedAlarmsOption(): boolean {
		return this.selectedGroup.status === GroupStatus.Partial;
	}

	private get showDisarmedAlarmsContent(): boolean {
		return this.selectedOption === "view-disarmed-alarms";
	}

	/**
	 * Gets the min/max date/time for the datetime picker
	 * Limit disarming to 24 hours unless user has canDisarmExtended
	 *
	 * @returns {min: now, max:now+24h} if disarming without extended permission
	 * @returns {min: now, max:null} otherwise
	 * @private
	 */
	private get dateBounds(): { min: string; max: string } {
		const now = new Date(Date.now());
		const nowPlus24h = new Date(now.getTime() + 1000 * 60 * 60 * 24);

		if (!this.canDisarmExtended) {
			return { min: now.toISOString(), max: nowPlus24h.toISOString() };
		}

		return { min: now.toISOString(), max: null };
	}

	private async handleDisarmClicked(): Promise<void> {
		const disarmUntil = new Date(this.disarmUntil);
		await api.maskArea(this.selectedGroup.groupID, this.disarmReason, disarmUntil, this.region?.url);
		this.updateTree();
	}

	private handleOnTestStarted(): void {
		this.setGroupIsOnTestOverride({
			groupId: this.selectedGroup.groupID,
			onTest: true,
			regionId: this.region?.regionId ?? getLocalRegion().regionId
		});
		this.updateTree();
	}

	@Emit("tree-updated")
	private updateTree(): AreaSelected {
		return { groupID: this.selectedGroup.groupID, regionID: this.region?.regionId };
	}
}
